import { cloneDeep } from "lodash"
import Modal from "modal-react-native-web"
import React, { useCallback, useEffect, useMemo, useState } from "react"
import {
  Platform,
  StyleSheet,
  Text,
  TouchableOpacity,
  View,
} from "react-native"
import { Overlay } from "react-native-elements"
import Icon from "react-native-vector-icons/AntDesign"
import Icon2 from "react-native-vector-icons/Feather"
import { useDispatch, useSelector } from "react-redux"
import { useMediaQuery } from "react-responsive"

import Base, { Actions, Api, Firebase } from "../../base"
import { Alert, LoadingView } from "../common"
import VenueNameBanner from "../orders/VenueNameBanner"
import MenuView from "./menu/toplevel/MenuView"
import SettingsHeader from "./SettingsHeader"
import VenueView from "./VenueView"

function settingsHeader(navigation, logoutUser) {
  return {
    ...Base.constants.headerOptions(false, "Settings"),
    headerLeft: () => (
      <TouchableOpacity
        style={Base.styles.headerButton}
        onPress={() => navigation.goBack()}
      >
        <Icon name="arrowleft" size={24} color="white" />
      </TouchableOpacity>
    ),
    headerRight: () => (
      <TouchableOpacity
        style={Base.styles.headerButton}
        onPress={() => logoutUser()}
      >
        <Icon2 name="log-out" size={24} color="white" />
      </TouchableOpacity>
    ),
  }
}

export default function SettingsScreen(props) {
  const [loading, setLoading] = useState(null)
  const service = useSelector(state => state.service)
  const venue = useSelector(state => state.venue)
  const isMobile = useMediaQuery(Base.constants.mobile)
  const dispatch = useDispatch()

  useEffect(() => {
    getVenue()
  }, [getVenue])

  const getVenue = useCallback(async () => {
    const v = await Api.getVenue(venue)
    if (v && v._id) {
      dispatch(Actions.setMyVenue(v))
    }

    const business = await Api.getBusiness()
    if (business && business._id) {
      console.log(JSON.stringify(business))
      dispatch(Actions.setBusiness(business))
    }
  }, [dispatch, venue])

  async function logout() {
    await Base.functions.logoutUser(success => {
      props.navigation.goBack()
      Firebase.logEvent("logout_settings")
    })
  }
  props.navigation.setOptions(settingsHeader(props.navigation, logout))

  const isFlooid = service?.menu?.provider === "Flooid"

  async function toggleService(d) {
    const s = { ...d, isEnabled: !d.isEnabled }
    setLoading("Updating Venue...")
    const success = await Api.setServiceStatus(venue, s)
    if (!success || success === null) {
      Alert.show(
        "Hold Up!",
        "\nUnable to update this service at the moment, please check your connection and try again.",
      )
      setLoading(null)
      return
    }
    dispatch(Actions.setService(s))
    setLoading(null)
  }

  async function onStatusChange(item, status) {
    try {
      setLoading("Updating Status...")
      const success = await Api.updateMenuStatus(item, status)
      if (!success || success === null) {
        Alert.show(
          "Hold Up!",
          "Unable to update this item at the moment, please check your connection and try again.",
          "OK",
          () => {
            setLoading(null)
          },
        )
        return
      }

      const key =
        item.__type === "MenuItem"
          ? isFlooid
            ? "active"
            : "isActive"
          : "enabled"
      const menu = Base.functions.toggleItemValue(
        { ...service.menu },
        item._id,
        key,
        status,
      )
      dispatch(Actions.setService({ ...service, menu }))
      setLoading(null)
    } catch (error) {
      console.log(error)
    }
  }

  const containerStyle = useMemo(
    () => [styles.flexContainer, isMobile ? styles.column : styles.row],
    [isMobile],
  )

  if (!service) {
    return <View />
  }

  return (
    <View style={styles.main}>
      <View style={containerStyle}>
        <SettingsHeader
          isMobile={isMobile}
          service={service}
          venue={venue}
          onServiceChanged={_service => dispatch(Actions.setService(_service))}
          onStatusChanged={toggleService}
        />
        <View style={styles.flexContainer}>
          {!isMobile && <VenueNameBanner venue={venue} />}
          <View style={containerStyle}>
            <VenueView
              service={service}
              toggleService={toggleService}
              setLoading={setLoading}
            />
            {!isMobile && (
              <MenuView
                menu={service && service.menu}
                onStatusChange={onStatusChange}
                topLevel={true}
              />
            )}
          </View>
        </View>
      </View>
      {Platform.OS === "web" ? (
        <Modal animationType="fade" transparent={true} visible={!!loading}>
          <LoadingView title={loading} />
        </Modal>
      ) : (
        <Overlay
          isVisible={!!loading}
          windowBackgroundColor="#00000097"
          overlayStyle={styles.overlay}
          animationType={"fade"}
          statusBarTranslucent={"true"}
        >
          <LoadingView title={loading} />
        </Overlay>
      )}
      {!!isMobile && (
        <View style={styles.footer}>
          <Text style={styles.versionNumber}>
            {Base.functions.versionNumber()}
          </Text>
        </View>
      )}
    </View>
  )
}

const styles = StyleSheet.create({
  main: {
    flex: 1,
    backgroundColor: Base.colours.appLightGrey,
  },

  column: {
    flexDirection: "column",
  },

  row: {
    flexDirection: "row",
  },

  flexContainer: { flex: 1 },

  content_sm: {
    marginHorizontal: Base.constants.hmargin.sm,
    paddingTop: 5,
  },

  content_lg: {
    flex: 1,
    flexDirection: "row",
  },

  openingHours_lg: {
    backgroundColor: Base.colours.appLightGrey,
    paddingHorizontal: Base.constants.hmargin.md,
  },

  footer: {
    backgroundColor: Base.colours.appLightGrey,
    borderTopColor: Base.colours.appSemiLightGrey,
    borderTopWidth: 1,
    paddingVertical: 8,
    paddingHorizontal: 16,
  },

  versionNumber: {
    textAlign: "right",
  },

  overlay: {
    backgroundColor: "transparent",
    shadowOpacity: 0,
    elevation: 0,
  },
})
